/**
 * 이메일 본문에서 주소변경 요청 내용을 추출.
 * 우선 휴리스틱 정규식으로 시도 → 관리자가 미리보기·수정 후 적용.
 *
 * 원격 LLM 없이 동작. (옵션: Cloud Functions에서 Gemini/Claude로 보강 가능)
 */

export interface ParsedAddressUpdate {
  studentName?: string;
  school?: string;
  guardian?: string;
  oldAddress?: string;
  newAddress?: string;
  phone?: string;
  intent: "address" | "delete";
  confidence: "high" | "medium" | "low";
  reason: string;
}

// 전출/전학/자퇴 등 — 주소변경이 아니라 학생 삭제(전출 처리) 의도
const DELETE_INTENT = /전출|전학|자퇴|퇴학|탈퇴|그만두|그만 ?둠|그만뒀|끊어|끊고|해지|중단|빼\s*주|삭제|이전\s*학교/;

const STUDENT_LINE = /학생\s*(?:이름|명)?\s*[:：]?\s*([가-힣]{2,5})/;
// 라벨 캡처가 보호자/학부모 같은 호칭을 학생 이름으로 오인하지 않도록 제외
const NOT_A_NAME = /^(보호자|학부모|부모님?|어머니|아버지|엄마|아빠)$/;
const SCHOOL_LINE = /학교\s*(?:명)?\s*[:：]?\s*([가-힣A-Za-z0-9]+(?:초|중|고|학교))/;
const GUARDIAN_LINE = /(?:보호자|학부모|부모님?)\s*[:：]?\s*([가-힣]{2,4})/;
const PHONE_LINE = /(01[016789][- ]?\d{3,4}[- ]?\d{4})/;
const OLD_ADDR = /(?:기존|이전|종전|예전|원래)\s*주소\s*[:：]?\s*(.+)/;
const NEW_ADDR = /(?:변경|새|신규|이사|신|바뀐)\s*주소\s*[:：]?\s*(.+)/;
// 시도 이름(있으면) + 시·군·구 + 나머지 — 지역에 매이지 않게. 예: "대구 달서구 월배로 12", "성남시 분당구 …", "달서구 월배로 12"
const ADDR_LIKE = /((?:(?:서울|부산|대구|인천|광주|대전|울산|세종|경기|강원|충청?북|충청?남|전라?북|전라?남|경상?북|경상?남|제주)[가-힣]*\s*)?[가-힣]+(?:시|군|구)\s+[가-힣0-9]+(?:로|길|동|읍|면|리)[^\n]*)/;
// 시·구 없이 도로명부터 쓴 주소. 예: "해운대로 468, OO아파트 101동 1404호" — 시·도는 지도 찾기 서버가 업체 담당 시·도로 붙인다
const ROAD_ONLY = /([가-힣0-9]+(?:[·.][가-힣0-9]+)*(?:로|길)\s*\d+(?:-\d+)?[^\n]*)/;

export function parseAddressEmail(body: string): ParsedAddressUpdate {
  const text = body.replace(/\r/g, "");
  const out: ParsedAddressUpdate = { intent: "address", confidence: "low", reason: "" };

  const lines = text.split("\n");

  // "새 주소:"처럼 라벨로 명시된 새 주소인지 — 라벨 없는 폴백(식별용 현재 주소)과 구분
  let newAddressLabeled = false;

  for (const line of lines) {
    if (!out.studentName) {
      const m = line.match(STUDENT_LINE);
      if (m && !NOT_A_NAME.test(m[1])) out.studentName = m[1];
    }
    if (!out.school) {
      const m = line.match(SCHOOL_LINE);
      if (m) out.school = m[1];
    }
    if (!out.guardian) {
      const m = line.match(GUARDIAN_LINE);
      if (m) out.guardian = m[1];
    }
    if (!out.phone) {
      const m = line.match(PHONE_LINE);
      if (m) out.phone = m[1].replace(/\s/g, "-");
    }
    if (!out.oldAddress) {
      const m = line.match(OLD_ADDR);
      if (m) out.oldAddress = m[1].trim();
    }
    if (!out.newAddress) {
      const m = line.match(NEW_ADDR);
      if (m) { out.newAddress = m[1].trim(); newAddressLabeled = true; }
    }
  }

  // 라벨 없이 주소만 있는 경우 — 전체 본문에서 "부산..." 패턴 2개 이상 찾기
  if (!out.newAddress) {
    const matches = text.match(new RegExp(ADDR_LIKE, "g"));
    if (matches && matches.length === 1) {
      out.newAddress = matches[0];
    } else if (matches && matches.length >= 2) {
      out.oldAddress = out.oldAddress || matches[0];
      out.newAddress = matches[matches.length - 1];
    } else {
      // "기존 주소:" 줄은 빼고, 도로명이 여럿이면 마지막 것을 새 주소로 본다(위 ADDR_LIKE와 같은 규칙)
      const rest = lines.filter((l) => !OLD_ADDR.test(l)).join("\n");
      const roads = rest.match(new RegExp(ROAD_ONLY, "g"));
      if (roads && roads.length) out.newAddress = roads[roads.length - 1].trim();
    }
  }

  // 전출/전학 등 → 삭제 의도. 단, "새 주소:"로 명시된 주소가 있을 때만 주소변경 우선.
  // (전출 메시지에 든 주소는 대개 학생 식별용 현재 주소이므로 라벨 없는 폴백 주소로는 의도를 뒤집지 않는다)
  if (DELETE_INTENT.test(text) && !newAddressLabeled) out.intent = "delete";

  // 신뢰도 판정
  let score = 0;
  if (out.studentName) score++;
  if (out.school) score++;
  if (out.newAddress) score += 2;
  if (out.phone) score++;
  out.confidence = score >= 4 ? "high" : score >= 2 ? "medium" : "low";
  out.reason = out.intent === "delete"
    ? `전출/삭제 요청 · 학생=${!!out.studentName}, 학교=${!!out.school}, 전화=${!!out.phone}`
    : `학생=${!!out.studentName}, 학교=${!!out.school}, 새주소=${!!out.newAddress}, 전화=${!!out.phone}`;

  return out;
}

const norm = (s?: string) => (s || "").replace(/\s/g, "");

// 학교명을 메시지에서 찾기 위한 여러 표기형 생성. 예: "구덕고등학교" → {구덕고등학교, 구덕고, 구덕}
function schoolForms(school: string): string[] {
  const f = new Set<string>();
  const s = norm(school);
  if (!s) return [];
  f.add(s);
  f.add(s.replace(/초등학교$/, "초").replace(/중학교$/, "중").replace(/고등학교$/, "고"));
  f.add(s.replace(/(초등학교|중학교|고등학교|학교)$/, ""));
  return [...f].filter((x) => x.length >= 2);
}

/**
 * 메시지 본문을 학생 명단과 역방향으로 대조해 매칭.
 * (라벨 파싱에 의존하지 않고, 본문에 학생 이름·학교·전화가 들어있는지 직접 스캔)
 * 이름 또는 전화가 일치하는 후보만 반환해 같은 학교 전원이 뜨는 것을 방지한다.
 */
export function matchStudent(
  body: string,
  candidates: { id: string; name: string; school: string; phone: string; guardian?: string }[],
): { id: string; score: number }[] {
  const text = norm(body);
  const bodyDigits = body.replace(/\D/g, "");
  const out: { id: string; score: number }[] = [];
  for (const c of candidates) {
    let score = 0;
    const nameHit = c.name && norm(c.name).length >= 2 && text.includes(norm(c.name));
    const phoneDigits = (c.phone || "").replace(/\D/g, "");
    const phoneHit = phoneDigits.length >= 8 && bodyDigits.includes(phoneDigits);
    const schoolHit = schoolForms(c.school).some((f) => text.includes(f));
    const guardianHit = !!c.guardian && norm(c.guardian).length >= 2 && text.includes(norm(c.guardian));
    if (nameHit) score += 60;
    if (schoolHit) score += 30;
    if (phoneHit) score += 40;
    if (guardianHit) score += 20;
    // 이름/전화/보호자 중 하나라도 맞아야 후보 (학교만 맞는 전원 매칭 방지)
    if (nameHit || phoneHit || guardianHit) out.push({ id: c.id, score });
  }
  out.sort((a, b) => b.score - a.score);
  return out.slice(0, 5);
}
